import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  entitlementService,
  PlanEntitlementDto,
  SubscriptionPlanDto,
} from '../api/adminServices'
import { useToast } from './useToast'
import { EmptyState, ErrorCard, TableSkeleton } from './Feedback'

interface Props {
  plan: SubscriptionPlanDto | null
  open: boolean
  onClose: () => void
}

/**
 * Per-plan add-on (entitlement) manager. Lists every entitlement defined for the plan's account
 * type and lets the admin toggle each on/off for this plan (and set a quota for LIMIT-type
 * entitlements). Absence of an explicit row falls back to the entitlement's default.
 */
export default function PlanEntitlementsDialog({ plan, open, onClose }: Props) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [limitDrafts, setLimitDrafts] = useState<Record<string, string>>({})

  const planId = plan?.id
  const queryKey = ['plan-entitlements', planId]

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => entitlementService.listForPlan(planId!),
    enabled: open && !!planId,
  })

  const setMutation = useMutation({
    mutationFn: ({
      entitlementId,
      enabled,
      limitValue,
    }: {
      entitlementId: string
      enabled: boolean
      limitValue?: number | null
    }) => entitlementService.setForPlan(planId!, entitlementId, { enabled, limitValue }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success('Add-on updated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to update add-on'),
  })

  const rows: PlanEntitlementDto[] = data ?? []

  const handleToggle = (row: PlanEntitlementDto, enabled: boolean) => {
    setMutation.mutate({ entitlementId: row.entitlementId, enabled, limitValue: row.limitValue ?? undefined })
  }

  const handleSaveLimit = (row: PlanEntitlementDto) => {
    const raw = limitDrafts[row.entitlementId]
    const parsed = raw === '' || raw == null ? null : Number(raw)
    if (parsed != null && !Number.isFinite(parsed)) {
      toast.error('Limit must be a number')
      return
    }
    setMutation.mutate({ entitlementId: row.entitlementId, enabled: row.enabled, limitValue: parsed })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add-ons — {plan?.name}
        {plan?.accountType && (
          <Chip
            label={plan.accountType}
            size="small"
            color={plan.accountType === 'SHIPPER' ? 'info' : 'secondary'}
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enable or disable feature add-ons for this plan. Changes take effect immediately for
          accounts on this plan.
        </Typography>

        {isLoading ? (
          <TableSkeleton rows={4} columns={4} />
        ) : isError ? (
          <ErrorCard
            title="Failed to load add-ons"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No entitlements defined for this account type"
            description="Create entitlements in the Add-ons (Entitlements) page first."
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Feature</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell>Limit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const draft =
                    limitDrafts[row.entitlementId] ??
                    (row.limitValue != null ? String(row.limitValue) : '')
                  return (
                    <TableRow key={row.entitlementId} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {row.entitlementKey}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={row.enabled}
                          onChange={(e) => handleToggle(row, e.target.checked)}
                          disabled={setMutation.isPending}
                        />
                      </TableCell>
                      <TableCell>
                        {row.valueType === 'LIMIT' ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              type="number"
                              size="small"
                              placeholder="Unlimited"
                              value={draft}
                              onChange={(e) =>
                                setLimitDrafts((d) => ({ ...d, [row.entitlementId]: e.target.value }))
                              }
                              sx={{ width: 120 }}
                              inputProps={{ min: 0 }}
                            />
                            <Button size="small" onClick={() => handleSaveLimit(row)} disabled={setMutation.isPending}>
                              Save
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {setMutation.isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {rows.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Add-ons apply to <strong>{plan?.accountType}</strong> plans. Define new add-ons and their
            defaults in the Add-ons page.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
